import { useCallback, useEffect, useState } from 'react';
import { alterarAjuste, verAjustes } from '../api/triviais';
import { numero, paraNumero } from '../formato';
import { useSessao } from '../sessao';
import {
  Area, Aviso, Carregando, CampoNumerico, Dialogo, ErroDaApi, RespostaDaAcao,
} from '../ui';

/**
 * Engrenagem › Ajustes do sistema (onda 290, 0244).
 *
 * «Isso pode ir para as configurações do sistema como ajuste» — Humberto,
 * 06/10/2026, sobre os 15 minutos em que quem lançou uma pesagem pode
 * corrigi-la sem motivo. Os parâmetros nascem por migração; aqui só se
 * muda o VALOR, dentro da faixa, com motivo — que fica na trilha.
 *
 * Todos leem (o laboratorista vê quanto tempo tem); só quem tem
 * `ajuste.alterar` (o administrador) muda.
 */
export function Ajustes() {
  const { pode } = useSessao();
  const [lista, setLista] = useState<any[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [editando, setEditando] = useState<any | null>(null);
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  const [gravando, setGravando] = useState(false);
  const [ok, setOk] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try { setLista(await verAjustes()); } catch (e) { setErro(e); }
  }, []);
  useEffect(() => { void carregar(); }, [carregar]);

  const podeAlterar = pode('ajuste.alterar');

  const abrir = (a: any) => {
    setEditando(a); setValor(String(Number(a.valor)).replace('.', ','));
    setMotivo(''); setErro(null); setOk(null);
  };
  const v = paraNumero(valor);
  const falta = v == null ? 'Digite o valor.'
    : editando && editando.minimo != null && v < Number(editando.minimo)
      ? `O mínimo é ${numero(editando.minimo, 0)}.`
    : editando && editando.maximo != null && v > Number(editando.maximo)
      ? `O máximo é ${numero(editando.maximo, 0)}.`
    : motivo.trim().length < 5 ? 'Diga por que o ajuste está mudando (motivo).' : null;

  const gravar = async () => {
    if (!editando || falta || gravando) return;
    setGravando(true); setErro(null);
    try {
      setLista(await alterarAjuste(editando.chave, { valor: v as number, motivo: motivo.trim() }));
      setOk(`«${editando.rotulo}» passou a ${numero(v, 0)}${editando.unidade ? ` ${editando.unidade}` : ''}.`);
      setEditando(null);
    } catch (e) { setErro(e); } finally { setGravando(false); }
  };

  if (erro && !lista) return <ErroDaApi erro={erro} />;
  if (!lista) return <Carregando texto="Abrindo os ajustes" />;

  return (
    <div className="tela ajustes ficha-carbon">
      <header className="fe-cabecalho">
        <div className="fe-titulo">
          <div>
            <h1>Ajustes do sistema</h1>
            <p className="sub">
              Parâmetros que a Testesolo ajusta sem mexer no código. Cada mudança pede
              motivo e fica na trilha de auditoria.
            </p>
          </div>
        </div>
      </header>
      <RespostaDaAcao erro={editando ? null : erro} ok={ok} />
      {!podeAlterar && (
        <Aviso tipo="info">
          Você pode consultar os ajustes. Mudá-los é de quem tem a permissão «Alterar
          os ajustes do sistema».
        </Aviso>
      )}
      {lista.map((a) => (
        <section key={a.chave} className="bloco fe-bloco dd-ajuste-sistema">
          <header className="fe-bloco-topo">
            <h2><span>{a.rotulo}</span></h2>
          </header>
          <div className="fe-miolo">
            <p className="sub">{a.descricao}</p>
            <dl className="fe-mosaico">
              <div className="destaque"><dt>Valor</dt>
                <dd>{numero(a.valor, 0)}{a.unidade ? ` ${a.unidade}` : ''}</dd></div>
              <div><dt>Padrão</dt><dd>{numero(a.padrao, 0)}{a.unidade ? ` ${a.unidade}` : ''}</dd></div>
              <div><dt>Faixa</dt>
                <dd>{a.minimo != null ? numero(a.minimo, 0) : '—'} a {a.maximo != null ? numero(a.maximo, 0) : '—'}</dd></div>
            </dl>
            <p className="sub">
              {a.alterado_em
                ? <>Alterado por {a.alterado_por_nome ?? '—'} em {new Date(a.alterado_em).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}.</>
                : 'Nunca alterado — vale o padrão.'}
            </p>
          </div>
          {podeAlterar && (
            <div className="fe-acoes-bloco">
              <button type="button" className="botao secundario" onClick={() => abrir(a)}>
                Alterar
              </button>
            </div>
          )}
        </section>
      ))}

      <Dialogo aberto={editando !== null} titulo={editando ? `Alterar «${editando.rotulo}»` : ''}
        rotulo="Ajustes do sistema" aoFechar={() => setEditando(null)}>
        {editando && (
          <>
            <div className="fe-campos">
              <CampoNumerico rotulo={`Valor${editando.unidade ? ` (${editando.unidade})` : ''}`}
                inteiro value={valor} onChange={(e) => setValor(e.target.value)}
                dica={`de ${editando.minimo ?? '—'} a ${editando.maximo ?? '—'}`} />
              <Area rotulo="Motivo" rows={2} value={motivo}
                onChange={(e) => setMotivo(e.target.value)} />
            </div>
            <RespostaDaAcao erro={erro} ok={null} />
            <div className="rodape-dialogo">
              <button type="button" className="botao secundario" onClick={() => setEditando(null)}>
                Cancelar
              </button>
              <button type="button" className="botao" aria-disabled={falta !== null || gravando}
                title={falta ?? undefined} onClick={() => void gravar()}>
                {gravando ? 'Gravando…' : 'Gravar'}
              </button>
            </div>
          </>
        )}
      </Dialogo>
    </div>
  );
}
